import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import type { InvoiceFormFields } from '@/modules/cart/domain/Cart';
import { addInvoice } from '@/modules/cart/infrastructure/CartReduxRepository/CartReduxRepository';
import { Feedback, FeedbackType } from '@/modules/perseo-billing/components';
import {
  DEFAULT_FORM_VALUES,
  DEFAULT_PAYLOAD,
  FEEDBACK_INFO_SUBTITLE,
  FEEDBACK_INFO_TITLE,
  FEEDBACK_SUBTITLE,
  LABEL_CTA_BUTTON,
  SUBMIT_TOAST_MESSAGE
} from '@/modules/perseo-billing/containers/payment-methods/components/need-invoice/form/constants';
import {
  needInvoiceFormSchema,
  yupResolver
} from '@/modules/perseo-billing/containers/payment-methods/components/need-invoice/form/need-invoice-form.billing.schema';
import { FormFields } from '@/modules/perseo-billing/containers/payment-methods/components/need-invoice/form/need-invoice-form-fields.component';
import { BuyerInformation } from '@/modules/perseo-billing/containers/payment-methods/components/need-invoice/invoice-buyer-information.component';
import { useGtmBillingProduct } from '@/modules/perseo-billing/hooks/use-gtm-billing';
import useToast from '@/modules/perseo-billing/hooks/use-toast';
import type { TInvoiceValues } from '@/modules/perseo-billing/interfaces';
import { setIsNeedInvoiceModalOpen } from '@/modules/perseo-billing/store/slices/billing';
import { Divider } from '@/modules/perseo-billing/ui/react/src/components/beta';
import { PdsButton } from '@/modules/perseo-billing/ui/react/src/components/gamma';
import { isDeviceKiosco } from '@/modules/perseo-billing/utils';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import useRegions from '@/sections/region/hooks/useRegions';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

export const NeedInvoiceForm = () => {
  const { data: cart } = useAppSelector((state) => state.cart);
  const shippingAddress = cart?.shippingAddress;
  const billingAddress = cart?.billingAddress;
  const isKiosco = isDeviceKiosco();

  const dispatch = useAppDispatch();
  const toast = useToast();

  const { getRegionAndCommuneByIds } = useRegions();

  const customerContext = useCustomerContext();
  const customerData = customerContext?.customer?.data;
  const customerRut = customerData?.rut ?? '';
  const customerName = customerData?.firstName ?? '';
  const customerLastName = customerData?.lastName ?? '';
  const customerEmail = customerData?.email ?? '';
  const customerPhone = customerData?.phone ?? '';
  const customerAddresses = customerData?.addresses ?? [];
  const defaultCustomerAddress = customerAddresses?.find(
    (address) => address?.isDefault
  );
  const customerAddressAlias = defaultCustomerAddress?.alias ?? '';

  const { saveInvoiceDataGtm } = useGtmBillingProduct();

  const invoiceForm = useForm<TInvoiceValues>({
    resolver: yupResolver(needInvoiceFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'all'
  });

  const { handleSubmit } = invoiceForm;

  const onSubmit: SubmitHandler<TInvoiceValues> = (data) => {
    const regionId = data?.regionId ?? '';
    const communeId = data?.communeId ?? '';
    const region = getRegionAndCommuneByIds(regionId, communeId);
    const communes = region?.communes ?? [];
    const communeIdNumber = Number(communeId) ?? 0;
    const commune = communes?.find(
      (commune) => commune?.id === communeIdNumber
    );

    const formValues = {
      apartment: data?.numberOfHouseApartmentOrOffice,
      commune: communeId,
      communeName: commune?.name,
      companyBusinessName: data?.officialName,
      companyIdentityDocumentNumber: data?.id,
      companyLineOfBusiness: data?.areaOfWork,
      companyName: data?.officialName,
      region: regionId,
      regionName: region?.name,
      streetName: data?.address,
      streetNumber: String(data?.addressNumber),
      ...DEFAULT_PAYLOAD
    };

    const kioscoPayload: InvoiceFormFields = {
      additionalAddressInfo: shippingAddress?.additionalAddressInfo,
      alias: shippingAddress?.alias,
      companyEmail: shippingAddress?.email,
      customerRut: shippingAddress?.customerRut,
      email: shippingAddress?.email,
      firstName: shippingAddress?.firstName,
      lastName: shippingAddress?.lastName,
      phone: shippingAddress?.phone,
      ...formValues
    };

    const phone =
      shippingAddress?.phone ?? billingAddress?.phone ?? customerPhone;
    const phoneWithoutSpaces = phone?.replace(/\s/g, '') ?? '';
    const firstName =
      shippingAddress?.firstName ?? billingAddress?.firstName ?? customerName;
    const lastName =
      shippingAddress?.lastName ?? billingAddress?.lastName ?? customerLastName;
    const email =
      shippingAddress?.email ?? billingAddress?.email ?? customerEmail;
    const alias =
      shippingAddress?.alias ?? billingAddress?.alias ?? customerAddressAlias;
    const companyEmail =
      shippingAddress?.email ?? billingAddress?.email ?? customerEmail;
    const additionalAddressInfo =
      shippingAddress?.additionalAddressInfo ??
      billingAddress?.additionalAddressInfo;

    const payload: InvoiceFormFields = {
      additionalAddressInfo: additionalAddressInfo,
      alias: alias,
      companyEmail: companyEmail,
      customerRut: customerRut,
      email: email,
      firstName: firstName,
      lastName: lastName,
      phone: phoneWithoutSpaces,
      ...formValues
    };

    saveInvoiceDataGtm();
    dispatch(addInvoice(isKiosco ? kioscoPayload : payload));
    dispatch(setIsNeedInvoiceModalOpen(false));
    toast(SUBMIT_TOAST_MESSAGE, '', 'success');
  };

  return (
    <FormProvider {...invoiceForm}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="co-flex co-flex-col co-w-full co-overflow-y-hidden co-h-full tablet:co-max-h-[65vh] co-justify-between"
      >
        <div className="xl:co-h-600px co-flex co-flex-col co-gap-4 co-overflow-y-scroll tablet:co-px-4 co-py-4">
          <Feedback type={FeedbackType.WARNING} subtitle={FEEDBACK_SUBTITLE} />
          <FormFields />
          <Divider />
          <BuyerInformation />
          <Feedback
            type={FeedbackType.INFO}
            title={FEEDBACK_INFO_TITLE}
            subtitle={FEEDBACK_INFO_SUBTITLE}
          />
        </div>
        <div className="co-flex co-w-full co-h-auto co-p-3">
          <PdsButton
            typology="primary"
            disabled={!invoiceForm.formState.isValid}
            fullWidth
            type="submit"
          >
            {LABEL_CTA_BUTTON}
          </PdsButton>
        </div>
      </form>
      <LocalHookFormDevtool control={invoiceForm.control} />
    </FormProvider>
  );
};
